import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User } from 'oidc-client-ts';
import { OidcAuthService, OidcConfig, initializeAuth } from './oidc-client';

interface AuthUser {
  id: string;
  userName: string;
  email: string;
  name: string;
  roles: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  renewToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an OidcAuthProvider');
  }
  return context;
}

interface OidcAuthProviderProps {
  children: ReactNode;
  config: OidcConfig;
  onAuthRequired?: () => void;
}

export function OidcAuthProvider({ children, config, onAuthRequired }: OidcAuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authService] = useState<OidcAuthService>(() => initializeAuth(config));

  // Convert OIDC user to our AuthUser format
  const convertUser = useCallback((oidcUser: User | null): AuthUser | null => {
    if (!oidcUser || !oidcUser.profile) {
      return null;
    }

    const profile = oidcUser.profile;
    return {
      id: profile.sub || '',
      userName: profile.preferred_username || profile.name || '',
      email: profile.email || '',
      name: profile.name || '',
      roles: profile.role ? (Array.isArray(profile.role) ? profile.role : [profile.role]) : [],
    };
  }, []);

  // Check authentication status
  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      const oidcUser = await authService.getUser();

      if (oidcUser && !oidcUser.expired) {
        const authUser = convertUser(oidcUser);
        setUser(authUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);

        // If authentication is required and user is not authenticated
        if (onAuthRequired) {
          onAuthRequired();
        }
      }
    } catch (error) {
      console.error('Check auth error:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, [authService, convertUser, onAuthRequired]);

  // Initialize authentication
  useEffect(() => {
    checkAuth();

    // Setup event listeners
    const userManager = authService.getUserManager();

    const handleUserLoaded = (oidcUser: User) => {
      const authUser = convertUser(oidcUser);
      setUser(authUser);
      setIsAuthenticated(true);
    };

    const handleUserUnloaded = () => {
      setUser(null);
      setIsAuthenticated(false);
    };

    const handleUserSignedOut = () => {
      setUser(null);
      setIsAuthenticated(false);
      if (onAuthRequired) {
        onAuthRequired();
      }
    };

    userManager.events.addUserLoaded(handleUserLoaded);
    userManager.events.addUserUnloaded(handleUserUnloaded);
    userManager.events.addUserSignedOut(handleUserSignedOut);

    // Cleanup
    return () => {
      userManager.events.removeUserLoaded(handleUserLoaded);
      userManager.events.removeUserUnloaded(handleUserUnloaded);
      userManager.events.removeUserSignedOut(handleUserSignedOut);
    };
  }, [authService, checkAuth, convertUser, onAuthRequired]);

  // Login method
  const login = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.login();
    } catch (error) {
      console.error('Login error:', error);
      setIsLoading(false);
      throw error;
    }
  }, [authService]);

  // Logout method
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, clear local state
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    }
  }, [authService]);

  // Get access token
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    return await authService.getAccessToken();
  }, [authService]);

  // Renew token
  const renewToken = useCallback(async () => {
    try {
      const oidcUser = await authService.renewToken();
      if (oidcUser) {
        const authUser = convertUser(oidcUser);
        setUser(authUser);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Token renewal error:', error);
      throw error;
    }
  }, [authService, convertUser]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    getAccessToken,
    renewToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}